/**
 * 🐦 ANXIOUS BIRD TEMPERAMENT
 * Nervous energy builds until full PANIC FLIGHT!
 * Feathers ruffling, eyes darting, then CHAOS!
 *
 * Created by Tom & Azura - January 14, 2026
 */

import type { Temperament } from '../types';

export const AnxiousBird: Temperament = {
  id: 'anxious-bird',
  name: 'Anxious Bird',
  emoji: '🐦',
  description: 'Nervous energy builds slowly until full panic flight! Feathers everywhere!',

  // ============================================================
  // MOODS - Building toward PANIC
  // ============================================================

  moods: ['roosting', 'alert', 'nervous', 'frantic', 'panic', 'exhausted'],
  initialMood: 'roosting',

  // ============================================================
  // MOOD TRANSITIONS
  // ============================================================

  moodMatrix: {
    roosting: [
      { mood: 'roosting', weight: 0.6 },
      { mood: 'alert', weight: 0.4 },
    ],
    alert: [
      { mood: 'roosting', weight: 0.15 },
      { mood: 'alert', weight: 0.35 },
      { mood: 'nervous', weight: 0.5 },
    ],
    nervous: [
      { mood: 'alert', weight: 0.2 },
      { mood: 'nervous', weight: 0.3 },
      { mood: 'frantic', weight: 0.5 },
    ],
    frantic: [
      { mood: 'nervous', weight: 0.15 },
      { mood: 'frantic', weight: 0.35 },
      { mood: 'panic', weight: 0.5 },
    ],
    panic: [
      { mood: 'panic', weight: 0.6 },
      { mood: 'exhausted', weight: 0.4 },
    ],
    exhausted: [
      { mood: 'exhausted', weight: 0.3 },
      { mood: 'roosting', weight: 0.6 },
      { mood: 'alert', weight: 0.1 },
    ],
  },

  // ============================================================
  // MOOD DURATIONS
  // ============================================================

  moodDurations: {
    roosting:  { base: 2500, variance: 3000 },
    alert:     { base: 400, variance: 600 },
    nervous:   { base: 300, variance: 500 },
    frantic:   { base: 200, variance: 300 },
    panic:     { base: 150, variance: 250 },
    exhausted: { base: 1000, variance: 1500 },
  },

  moodConstraints: {
    panic: {
      minDuration: 2000,
      maxDuration: 5000,
    },
  },

  // ============================================================
  // ENERGY SYSTEM - Builds anxiety!
  // ============================================================

  energy: {
    max: 100,
    initial: 0,
    regenRate: 6,

    moodDrain: {
      roosting:  -2,    // Slowly builds anxiety even at rest
      alert:     -3,    // Building...
      nervous:   -2,    // Building...
      frantic:   -1,    // Almost there...
      panic:     30,    // Burns fast during panic!
      exhausted: -4,    // Recovers
    },

    thresholds: {
      exhausted: 10,
      low: 25,
      high: 50,
      explosive: 70,
    },

    restingMoods: ['roosting', 'exhausted', 'alert', 'nervous'],
  },

  // ============================================================
  // EXPRESSIONS
  // ============================================================

  expressions: {
    roosting: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 400,
      jitter: 1,
      glowIntensity: 0.1,
      glowColor: 'rgba(135, 206, 250, 0.3)',
    },
    alert: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -1, z: 0 },
      scale: 1,
      transitionMs: 150,
      jitter: 3,
      glowIntensity: 0.2,
      glowColor: 'rgba(135, 206, 250, 0.4)',
    },
    nervous: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 100,
      jitter: 6,
      glowIntensity: 0.4,
      glowColor: 'rgba(255, 200, 100, 0.5)',
    },
    frantic: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1.01,
      transitionMs: 60,
      jitter: 10,
      glowIntensity: 0.6,
      glowColor: 'rgba(255, 150, 50, 0.7)',
    },
    panic: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -2, z: 0 },
      scale: 1.03,
      transitionMs: 40,
      jitter: 8,
      glowIntensity: 1,
      glowColor: 'rgba(255, 100, 100, 0.9)',
      cssClass: 'panic',
    },
    exhausted: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 3, z: 0 },
      scale: 0.97,
      transitionMs: 500,
      jitter: 0.5,
      glowIntensity: 0.1,
      glowColor: 'rgba(135, 206, 250, 0.2)',
    },
  },

  // ============================================================
  // CORNER BOUNCING (for panic mode - subtle!)
  // ============================================================

  corners: {
    center: {
      transform: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      bulge: { x: 50, y: 50 },
    },
    topLeft: {
      transform: { x: -3, y: 3, z: -1 },
      translation: { x: -5, y: -6, z: 0 },
      bulge: { x: 20, y: 20 },
    },
    topRight: {
      transform: { x: -3, y: -3, z: 1 },
      translation: { x: 5, y: -6, z: 0 },
      bulge: { x: 80, y: 20 },
    },
    bottomLeft: {
      transform: { x: 3, y: 3, z: 1 },
      translation: { x: -5, y: 4, z: 0 },
      bulge: { x: 20, y: 80 },
    },
    bottomRight: {
      transform: { x: 3, y: -3, z: -1 },
      translation: { x: 5, y: 4, z: 0 },
      bulge: { x: 80, y: 80 },
    },
  },

  bouncingMoods: ['panic', 'frantic'],

  bounceIntervals: {
    panic: 120,
    frantic: 250,
  },

  // ============================================================
  // STIMULI HANDLERS
  // ============================================================

  stimulusHandlers: {
    time: (_stimulus, currentMood, energy) => {
      if (currentMood === 'roosting' && energy < 15) {
        return { energyDelta: 4 };
      }
      if (currentMood === 'roosting' && energy >= 15) {
        return { transitionTo: 'alert', energyDelta: 3 };
      }
      if (currentMood === 'alert') {
        return { transitionTo: 'nervous', energyDelta: 2 };
      }
      return null;
    },

    poke: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Poking SCARES the bird - fast escalation!
      if (currentMood === 'roosting') {
        return { transitionTo: 'alert', energyDelta: 20 * intensity };
      }
      if (currentMood === 'alert') {
        return { transitionTo: 'nervous', energyDelta: 20 * intensity };
      }
      if (currentMood === 'nervous') {
        return { transitionTo: 'frantic', energyDelta: 25 * intensity };
      }
      if (currentMood === 'frantic') {
        return { transitionTo: 'panic', energyDelta: 30 * intensity, forceDuration: 2000 };
      }
      if (currentMood === 'exhausted') {
        return { transitionTo: 'alert', energyDelta: 15 * intensity };
      }
      return { energyDelta: 15 * intensity };
    },

    shake: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Shaking TERRIFIES the bird!
      if (currentMood === 'roosting' || currentMood === 'alert') {
        return { transitionTo: 'frantic', energyDelta: 35 * intensity };
      }
      if (currentMood === 'nervous' || currentMood === 'frantic' || energy > 40) {
        return { transitionTo: 'panic', energyDelta: 40 * intensity, forceDuration: 3000 };
      }
      return { transitionTo: 'frantic', energyDelta: 30 * intensity };
    },
  },

  // ============================================================
  // MOOD INDICATORS
  // ============================================================

  moodIndicators: {
    roosting: {
      emoji: '🪺',
      text: 'zzz',
      label: 'peacefully roosting...',
    },
    alert: {
      emoji: '👀',
      text: '?',
      label: 'what was that?',
    },
    nervous: {
      emoji: '😰',
      text: '??',
      label: '*feathers ruffling*',
    },
    frantic: {
      emoji: '😱',
      text: '?!',
      label: 'SOMETHING IS WRONG',
    },
    panic: {
      emoji: '🪶',
      text: '!!!',
      label: '🐦 PANIC FLIGHT! 🪶',
    },
    exhausted: {
      emoji: '😮‍💨',
      text: '...',
      label: '*panting*',
    },
  },
};
